import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppBackButton from '../common/AppBackButton';
import {
  dismissTrend,
  fetchCurrentTrends,
  trendCtaClick,
  trendSourceClick,
  type RecruiterTrendCard,
} from './activityFeedApi';
import { defaultTrendCtaLabel, trendCtaHref } from '../../utils/trendCta';
import './InnerPages.css';

function formatTrendDate(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

export default function TrendsListPage() {
  const navigate = useNavigate();
  const [trends, setTrends] = useState<RecruiterTrendCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await fetchCurrentTrends();
      setTrends(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load trends');
      setTrends([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDismiss(trendId: string) {
    try {
      await dismissTrend(trendId);
      void load();
    } catch (e) {
      console.error('Failed to dismiss trend', e);
    }
  }

  async function handleCta(trend: RecruiterTrendCard) {
    try {
      await trendCtaClick(trend.id);
    } catch (e) {
      console.error('Failed to log trend CTA click', e);
    }
    navigate(trendCtaHref(trend.cta_type, trend.cta_target));
  }

  async function handleSource(trend: RecruiterTrendCard) {
    if (!trend.source_url) return;
    try {
      await trendSourceClick(trend.id);
    } catch (e) {
      console.error('Failed to log trend source click', e);
    }
    window.open(trend.source_url, '_blank', 'noreferrer noopener');
  }

  return (
    <div className="dashboard-inner-page">
      <AppBackButton />

      <header className="mb-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
          Recruiter Trends
        </p>
        <h1 className="mt-1 text-xl font-bold text-slate-900 sm:text-2xl">Current Trends</h1>
        <p className="mt-1 max-w-2xl text-sm text-slate-600">
          What recruiters are paying attention to on SkipScreen — synthesized weekly so you know
          exactly where to invest next.
        </p>
      </header>

      {trends === null ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      ) : trends.length === 0 ? (
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-6 text-center">
          <p className="text-sm font-medium text-slate-700">No active trends right now.</p>
          <p className="mt-1 text-xs text-slate-500">Check back after the next weekly digest.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {trends.map((trend) => {
            const ctaLabel = trend.cta_label?.trim() || defaultTrendCtaLabel(trend.cta_type);

            return (
              <article
                key={trend.id}
                className="relative rounded-xl border border-emerald-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
              >
                <button
                  type="button"
                  className="absolute right-3 top-3 rounded p-1 text-xs leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Dismiss trend"
                  onClick={() => void handleDismiss(trend.id)}
                >
                  ✕
                </button>

                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                    {formatTrendDate(trend.published_at)} · Trend
                  </span>
                  {trend.source_name ? (
                    <button
                      type="button"
                      className="text-[10px] font-medium text-slate-500 underline-offset-2 hover:text-emerald-700 hover:underline"
                      onClick={() => void handleSource(trend)}
                    >
                      Source: {trend.source_name}
                    </button>
                  ) : null}
                </div>

                <h2 className="mt-1 pr-8 text-base font-semibold leading-snug text-slate-900">
                  {trend.title}
                </h2>

                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                  {trend.trend_summary}
                </p>

                {trend.action_text ? (
                  <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50/60 px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                      What to do
                    </p>
                    <p className="mt-0.5 text-[13px] leading-snug text-emerald-950">
                      {trend.action_text}
                    </p>
                  </div>
                ) : null}

                <div className="mt-3 flex items-center justify-end">
                  <button
                    type="button"
                    className="v1-btn v1-btn-primary v1-btn-sm"
                    onClick={() => void handleCta(trend)}
                  >
                    {ctaLabel}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
